import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { ExerciseRenderer } from '@/components/exercises/renderers/ExerciseRenderer'
import { exerciseRunsApi, isCheckConflictError, type ExerciseRun } from '@/api/exercises'
import { getErrorMessage } from '@/utils/error'

/**
 * Читаемый ответ ученика в режиме проверки.
 *
 * Ответ рисуют те же рендереры, что и решение, только с `readOnly` — а он у
 * всех восемнадцати типов означает одно и то же: MUI-контролы внутри
 * выключаются через `disabled`. Выключенное MUI красит
 * `-webkit-text-fill-color` и полупрозрачностью, и написанное учеником
 * выглядело бледнее подсказки: учитель читал ответ как placeholder и считал,
 * что ученик не ответил вовсе.
 *
 * Возвращаем обычный цвет и непрозрачность, а «править нельзя» оставляем за
 * подложкой и курсором. Правило одно на всю карточку, а не свой вид ответа на
 * каждый тип: рендереры разные, но выключаются одинаково — значит и лечится
 * это в одном месте. Тот же приём применён в просмотре задания
 * (`ExerciseFormDialog`).
 */
const readableStudentAnswerSx: SxProps<Theme> = {
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: 'unset',
    color: 'text.primary',
    cursor: 'default',
  },
  '& .MuiInputBase-root.Mui-disabled': { bgcolor: 'action.hover' },
  // Рамка выключенного поля почти сливается с фоном — без неё непонятно, где
  // кончается ответ на несколько абзацев
  '& .MuiInputBase-root.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
  '& .MuiFormLabel-root.Mui-disabled': { color: 'text.secondary' },
  '& .MuiFormHelperText-root.Mui-disabled': { color: 'text.secondary' },
  // Чипы — это ответ у половины типов: собранное предложение, разложенные по
  // группам слова, найденное слово в филворде, отмеченная ошибка
  '& .MuiChip-root.Mui-disabled': { opacity: 1 },
  '& .MuiChip-root.Mui-disabled .MuiChip-label': { color: 'text.primary' },
  // «Верно/неверно» — переключатели: без этого выбранный вариант не отличить
  '& .MuiToggleButton-root.Mui-disabled': { color: 'text.primary', opacity: 1 },
  '& .MuiToggleButton-root.Mui-selected.Mui-disabled': { color: 'primary.main' },
  '& .MuiFormControlLabel-label.Mui-disabled': { color: 'text.primary' },
  '& .MuiCheckbox-root.Mui-disabled, & .MuiRadio-root.Mui-disabled': { color: 'text.secondary' },
  // Отмеченный вариант обязан читаться отмеченным: серая галка рядом с серым
  // текстом ничем не отличается от пустого кружка
  '& .MuiCheckbox-root.Mui-checked.Mui-disabled, & .MuiRadio-root.Mui-checked.Mui-disabled': {
    color: 'primary.main',
  },
}

/** Какое из двух действий сейчас выполняется — кнопки блокируются обе, а надпись меняется у одной */
type SavingAction = 'grade' | 'comment'

/**
 * Проверка одной сданной работы: ответ ученика, подсказка автопроверки и
 * оценка учителя.
 *
 * Вынесено из страницы, чтобы то же самое открывалось прямо в уроке: работу,
 * сданную на занятии, учитель проверяет не уходя с урока.
 *
 * Слот `ai_review` уже на экране, но пока всегда пуст: контур подключается
 * позже, и переделывать проверку под него не придётся.
 */
export function ExerciseCheckForm({
  runId,
  onChecked,
}: {
  runId: number
  onChecked: () => void
}) {
  const { t } = useTranslation()

  const [run, setRun] = useState<ExerciseRun | null>(null)
  const [score, setScore] = useState(100)
  // Двигал ли учитель ползунок. Само положение ползунка баллом не является:
  // на работе без автопроверки он стоит на 100, и отправка «как есть» закрывала
  // бы со стопроцентным баллом работу, к которой учитель написал «переделай».
  const [scoreTouched, setScoreTouched] = useState(false)
  const [comment, setComment] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<SavingAction | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Конфликт версий — не ошибка ввода: учителю нужен не текст сервера, а
  // развилка «обновить и посмотреть, что записал коллега»
  const [conflict, setConflict] = useState(false)
  const [commentSaved, setCommentSaved] = useState(false)

  // Форма живёт и в модалке урока: она размонтируется сразу после сохранения,
  // и записывать в неё ответ уже некуда
  const aliveRef = useRef(true)

  useEffect(() => {
    aliveRef.current = true

    return () => {
      aliveRef.current = false
    }
  }, [])

  const applyLoaded = useCallback((loaded: ExerciseRun) => {
    setRun(loaded)
    // Балл автопроверки — стартовое положение ползунка: учителю остаётся поправить
    setScore(loaded.teacher_score_percent ?? loaded.auto_score_percent ?? 100)
    setScoreTouched(false)
    setComment(loaded.teacher_comment ?? '')
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setConflict(false)
    setCommentSaved(false)
    try {
      const loaded = await exerciseRunsApi.get(runId)

      if (!aliveRef.current) return

      applyLoaded(loaded)
    } catch (err) {
      if (aliveRef.current) setError(getErrorMessage(err) || t('common.error'))
    } finally {
      if (aliveRef.current) setLoading(false)
    }
  }, [applyLoaded, runId, t])

  useEffect(() => {
    if (Number.isFinite(runId)) {
      void load()

      return
    }

    setLoading(false)
    setError(t('common.error'))
  }, [load, runId, t])

  /**
   * Отправка вердикта.
   *
   * `withScore = false` — ветка «только комментарий»: сервер в ней не трогает
   * ни балл, ни статус и не шлёт ученику повторное «задание проверено».
   * Раньше балл уходил всегда, и комментарий к автопроверенной работе молча
   * превращал балл робота в ручную оценку учителя.
   */
  const save = async (withScore: boolean) => {
    if (!run) return

    setSaving(withScore ? 'grade' : 'comment')
    setError(null)
    setConflict(false)
    setCommentSaved(false)
    try {
      const updated = await exerciseRunsApi.check(
        run.id,
        withScore ? score : null,
        comment.trim() === '' ? null : comment,
        // Версия, по которой нарисован экран: сервер отобьёт запись поверх
        // чужого вердикта вместо того, чтобы затереть его молча
        run.updated_at,
      )

      if (withScore) {
        onChecked()

        return
      }

      if (!aliveRef.current) return

      // Из ответа берём только саму попытку: сменилась версия, и следующее
      // сохранение без неё упёрлось бы в свой же конфликт. Поле ввода при этом
      // не переписываем — пока летел запрос, учитель мог дописать фразу, и
      // серверная версия (без неё) молча стёрла бы дописанное. Всё, что он
      // допечатал, остаётся в поле и уедет следующим сохранением
      setRun(updated)
      setCommentSaved(true)
    } catch (err) {
      if (!aliveRef.current) return

      if (isCheckConflictError(err)) {
        setConflict(true)
      } else {
        setError(getErrorMessage(err) || t('common.error'))
      }
    } finally {
      if (aliveRef.current) setSaving(null)
    }
  }

  const exercise = run?.exercise
  const aiReview = run?.ai_review ?? null
  const isChecked = run?.status === 'checked'
  // Балл учителя главнее автопроверки: именно его учитель и переопределяет
  const currentScore = run?.teacher_score_percent ?? run?.score_percent ?? run?.auto_score_percent ?? null
  // Ставить балл есть с чего: либо учитель выбрал его сам, либо есть что
  // подтвердить — вердикт робота или прежняя оценка
  const canGrade = scoreTouched || currentScore !== null
  // Сохранять есть что ровно тогда, когда текст отличается от записанного —
  // включая очистку поля. Прежде пустой комментарий был запрещён, и убрать
  // ошибочно сохранённый текст можно было только кнопкой «Проверить», а она
  // закрывает работу баллом и шлёт ученику уведомление о проверке
  const trimmedComment = comment.trim()
  const canSaveComment = trimmedComment !== (run?.teacher_comment ?? '').trim()

  if (loading) {
    return <Skeleton variant="rounded" height={420} />
  }

  if (!run || !exercise) {
    return error ? <Alert severity="error">{error}</Alert> : null
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      {conflict && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => void load()}>
              {t('exercises.check.reload', 'Обновить работу')}
            </Button>
          }
        >
          {t(
            'exercises.check.conflict',
            'Работу уже проверил кто-то другой. Обновите страницу — и увидите текущую оценку.',
          )}
        </Alert>
      )}

      {commentSaved && (
        <Alert severity="success" onClose={() => setCommentSaved(false)}>
          {t('exercises.check.commentSaved', 'Комментарий сохранён. Балл и статус работы не изменились.')}
        </Alert>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip size="small" label={run.student?.name ?? `#${run.student_id}`} />
        <Chip size="small" label={t(`exercises.types.${exercise.type}.name`)} />
        <Chip size="small" label={t('exercises.run.attempt', { number: run.attempt_number })} variant="outlined" />
        {run.auto_score_percent !== null && (
          <Chip size="small" color="info" label={t('exercises.check.autoScore', { score: run.auto_score_percent })} />
        )}
      </Stack>

      {run.exercise_changed && <Alert severity="warning">{t('exercises.check.exerciseChanged')}</Alert>}

      {/* Работа уже с вердиктом — это не запрет, а исходная точка: балл
        переопределяется, комментарий дописывается. Показываем текущий балл,
        чтобы учитель видел, что именно он заменяет */}
      {isChecked && (
        <Alert severity="info">
          {t('exercises.check.alreadyChecked', {
            score: currentScore ?? '—',
            defaultValue: 'Работа уже проверена: текущий балл {{score}}%. Можно переоценить или дополнить комментарий.',
          })}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            {t('exercises.check.studentAnswer')}
          </Typography>
          <Box sx={readableStudentAnswerSx}>
            <ExerciseRenderer
              type={exercise.type}
              // Задание могли поправить после сдачи: проверять надо ту версию,
              // которую решал ученик, иначе его ответ ложится на чужой текст и
              // выглядит бессмыслицей
              content={run.content_snapshot ?? exercise.content}
              answers={run.answers ?? {}}
              onChange={() => undefined}
              readOnly
              runId={run.id}
              run={run}
              delegateId={run.delegate?.id ?? null}
              delegateStatus={run.delegate?.status ?? null}
            />
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <AutoAwesomeIcon fontSize="small" color="disabled" />
            <Typography variant="subtitle2">{t('exercises.check.aiReview')}</Typography>
          </Stack>
          {aiReview === null ? (
            <Typography variant="body2" color="text.secondary">
              {t('exercises.check.aiReviewEmpty')}
            </Typography>
          ) : (
            // Формат подсказки появится вместе с контуром; до тех пор
            // показываем текстовое поле, а не технический дамп объекта
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {typeof aiReview.summary === 'string' ? aiReview.summary : t('exercises.check.aiReviewEmpty')}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            {t('exercises.check.score')}
          </Typography>
          {/* Что заменяем — видно рядом с ползунком: иначе учитель двигает
            бегунок вслепую и не понимает, отличается ли новый балл от текущего */}
          {currentScore !== null && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('exercises.check.currentScore', {
                score: currentScore,
                defaultValue: 'Текущий балл: {{score}}%',
              })}
            </Typography>
          )}
          <Stack direction="row" spacing={2} alignItems="center">
            <Slider
              value={score}
              min={0}
              max={100}
              step={5}
              marks
              valueLabelDisplay="auto"
              onChange={(_, value) => {
                setScore(Number(value))
                setScoreTouched(true)
              }}
              sx={{ flex: 1 }}
            />
            <Typography sx={{ width: 56, textAlign: 'right' }}>{score}%</Typography>
          </Stack>

          <TextField
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 2 }}
            label={t('exercises.check.comment')}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            helperText={t(
              'exercises.check.commentHint',
              'Комментарий можно сохранить и не меняя балл',
            )}
          />

          {/* Два действия и два разных последствия — учитель видит их до
            нажатия. Замка на переоценку нет: сервер её разрешает, и запрещать
            в форме значило бы держать учителя при первом же промахе */}
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {canGrade
                ? t('exercises.check.gradeHint', {
                    score,
                    defaultValue:
                      'Проверить: работа закроется с баллом {{score}}%, ученик получит уведомление о проверке.',
                  })
                : t(
                    'exercises.check.scoreNotSet',
                    'Балл никто не ставил: подвиньте ползунок, чтобы оценить работу.',
                  )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t(
                'exercises.check.commentOnlyHint',
                'Сохранить комментарий: балл и статус не меняются, работа остаётся на проверке.',
              )}
            </Typography>

            <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                disabled={saving !== null || !canSaveComment}
                onClick={() => void save(false)}
              >
                {saving === 'comment'
                  ? t('common.loading')
                  : t('exercises.check.saveComment', 'Сохранить комментарий')}
              </Button>
              <Button
                variant="contained"
                disabled={saving !== null || !canGrade}
                onClick={() => void save(true)}
              >
                {saving === 'grade'
                  ? t('common.loading')
                  : isChecked
                    ? t('exercises.check.recheck', 'Переоценить')
                    : t('exercises.check.submit')}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  )
}
